import { FC, useEffect, useMemo, useState } from 'react'
import SVG from 'react-inlinesvg'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { changeGlobalRate } from '../../../services/commissionsService'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError } from '../../../utils/handleApiError'

interface Props {
  isOpen: boolean
  currentRate: string
  onClose: () => void
  onSaved: () => void
}

const genIdempotencyKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? (crypto as any).randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const ChangeGlobalRateModal: FC<Props> = ({ isOpen, currentRate, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const nowLocal = useMemo(() => new Date().toISOString().slice(0, 16), [])

  const [form, setForm] = useState({
    newValue: currentRate ?? '',
    effectiveFrom: nowLocal,
    reason: '',
  })

  useEffect(() => {
    if (!isOpen) return
    setIdempotencyKey(genIdempotencyKey())
    setForm({ newValue: currentRate ?? '', effectiveFrom: nowLocal, reason: '' })
  }, [isOpen, currentRate, nowLocal])

  if (!isOpen) return null

  const setField = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const isRetroactive = form.effectiveFrom && new Date(form.effectiveFrom) < new Date()

  const validate = () => {
    if (form.newValue === '' || isNaN(Number(form.newValue))) return 'New rate is required.'
    const n = Number(form.newValue)
    if (n < 0 || n > 100) return 'New rate must be between 0 and 100.'
    if (!form.effectiveFrom) return 'Effective-from date is required.'
    if (form.reason.trim().length < 10) return 'Reason must be at least 10 characters.'
    return null
  }

  const handleSave = async () => {
    const error = validate()
    if (error) {
      showErrorAlert({ title: 'Validation', message: error })
      return
    }
    setLoading(true)
    try {
      await changeGlobalRate({
        newValue: form.newValue,
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
        reason: form.reason.trim(),
        idempotencyKey,
      })
      onSaved()
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({ title: 'Error', message: 'Could not change the global rate.' }),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='nb-modal-overlay' onClick={onClose}>
      <div className='nb-invite-modal' onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className='admin-experience-details-header'>
          <h2 className='nb-invite-modal__title'>Change Global Rate</h2>
          <div className='rm-style-3 cursor-pointer' onClick={onClose}>
            <SVG src={toAbsoluteUrl('/media/svg/nobilis/closeX.svg')} className='rm-style-4' />
          </div>
        </div>

        <div className='team-admin-add-member__card'>
          <div className='form-field'>
            <label className='field-label'>New rate (%)</label>
            <input
              type='number'
              className='input'
              step='0.01'
              min={0}
              max={100}
              value={form.newValue}
              onChange={(e) => setField('newValue', e.target.value)}
            />
            <div className='field-hint text-muted'>
              From {currentRate}% to {form.newValue}% (preview)
            </div>
          </div>

          <div className='form-field'>
            <label className='field-label'>Effective from</label>
            <input
              type='datetime-local'
              className='input'
              value={form.effectiveFrom}
              onChange={(e) => setField('effectiveFrom', e.target.value)}
            />
            {isRetroactive && (
              <div className='field-hint text-warning'>
                ⚠ Retroactive: this changes the rate for new charges from this moment on, but does
                NOT recalculate payouts that were already frozen at their prior rate.
              </div>
            )}
          </div>

          <div className='form-field'>
            <label className='field-label'>Reason / note</label>
            <textarea
              className='input'
              rows={3}
              maxLength={500}
              minLength={10}
              value={form.reason}
              onChange={(e) => setField('reason', e.target.value)}
              placeholder='Why is this changing? (min 10 chars)'
            />
          </div>
        </div>

        <div className='form-actions'>
          <button type='button' className='btn-secondary' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='btn nb-btn-primary' disabled={loading} onClick={handleSave}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChangeGlobalRateModal
